import { machineReady, type FleetMachineHealth } from "@/context/fleet"

type Candidate = { name: string; health?: FleetMachineHealth }

/**
 * The machine to open when the URL names none. A ready machine always wins, and
 * among ready machines the current or remembered one; an unreachable remembered
 * machine must not hide a working one. Without a ready machine, the remembered
 * one (else the first) keeps the selection stable.
 */
export function chooseMachine<T extends Candidate>(
  machines: readonly T[],
  preferred: readonly (string | undefined)[],
): T | undefined {
  const named = preferred.flatMap((name) => machines.filter((machine) => !!name && machine.name === name))
  return named.find((machine) => machineReady(machine.health)) ??
    machines.find((machine) => machineReady(machine.health)) ??
    named[0] ??
    machines[0]
}

/**
 * Where bare /dashboard lands: a ready machine, or the machines page once every
 * machine's health is known and none is reachable. Undefined means still loading.
 */
export function dashboardHome(
  machines: readonly Candidate[],
  preferred: readonly (string | undefined)[],
  loading: boolean,
): { machine: string } | "machines" | undefined {
  const machine = chooseMachine(machines, preferred)
  if (machine && machineReady(machine.health)) return { machine: machine.name }
  if (loading || machines.some((candidate) => !candidate.health)) return undefined
  return "machines"
}
